import React from 'react';
import type { EChartsOption } from 'echarts';
import { EChartsWrapper } from '@/features/dashboard-charts';
import { useLocationBreakdown } from '../model/use-location-breakdown';
import type { DataLayer } from '../lib/location-popup.utils';

interface LocationPopupBreakdownProps {
  locationId: string;
  dataLayer: DataLayer;
  currentWeek: number;
  hasData: boolean;
}

export function LocationPopupBreakdown({ locationId, dataLayer, currentWeek, hasData }: LocationPopupBreakdownProps) {
  const { pieData, isLoading, breakdownType, weekNo } = useLocationBreakdown({
    locationId,
    dataLayer,
    currentWeek,
    hasData
  });

  const chartOption: EChartsOption = React.useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} MT ({d}%)'
      },
      series: [
        {
          type: 'pie',
          radius: ['25%', '50%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 3,
            borderColor: '#fff',
            borderWidth: 1
          },
          label: {
            show: true,
            position: 'outside',
            fontSize: 9,
            formatter: '{b}: {d}%'
          },
          labelLine: {
            show: true,
            length: 5,
            length2: 3
          },
          data: pieData.map((item) => ({
            name: item.name,
            value: item.value,
            itemStyle: { color: item.color }
          }))
        }
      ]
    }),
    [pieData]
  );

  if (isLoading) {
    return (
      <div className="bg-gray-50 rounded p-4 text-center text-sm text-gray-500">
        Loading breakdown data for week {weekNo || 'current'}...
      </div>
    );
  }

  if (pieData.length === 0) {
    return <div className="bg-gray-50 rounded p-4 text-center text-sm text-gray-500">No breakdown data available</div>;
  }

  return (
    <>
      <h4 className="text-sm font-medium text-gray-700 mb-2">
        Breakdown by {breakdownType === 'REO' ? 'Organization' : 'Product'}
      </h4>
      <EChartsWrapper option={chartOption} height={180} showExport={false} />
    </>
  );
}
